<template>
  <el-container v-loading="initializing" id="app" :direction="direction">
    <template v-if="user && !initializing">
      <el-header height="42px">
        <Nav aside="180px" />
      </el-header>

      <el-container v-if="project">
        <el-aside class="menu" width="200px">
          <Menu mode="vertical" />
        </el-aside>

        <el-main id="app-main">
          <router-view />

          <el-dialog
            v-if="invitation"
            title="Invitation"
            width="600px"
            :visible="true"
          >
            <TeamJoin :invitation="invitation" />
          </el-dialog>
        </el-main>
      </el-container>

      <el-main v-else id="start">
        <TeamJoin v-if="invitation" :invitation="invitation" />
        <AccountStart v-else />
      </el-main>
    </template>

    <el-main v-else-if="$route.query.signup" id="signup">
     <AccountSignup :step.sync="signupStep" class="form" />

      <div class="signin">
        <span>Already have an account?</span>
        <el-button type="text" @click="$router.push('/')">Sign in</el-button>
      </div>
    </el-main>

    <template v-else-if="!token">
      <el-main id="login">
        <AccountResetPassword
          v-if="$route.query.resetToken"
          :token="$route.query.resetToken"
          class="form"
        />

        <AccountLogin v-else class="form" />

        <div class="signup">
          <span>Don't have an account?</span>
          <el-button type="text" @click="$router.push('/?signup=true')">Open account</el-button>
        </div>
      </el-main>

      <el-aside width="50%">
        <img src="./assets/hero/login.svg" />
      </el-aside>
    </template>
  </el-container>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment-timezone'
import Nav from './components/Nav'
import Menu from './components/Menu'
import TeamJoin from './components/Team/Join'
import AccountLogin from './components/Account/Login'
import AccountSignup from './components/Account/Signup'
import AccountResetPassword from './components/Account/ResetPassword'
import AccountStart from './components/Account/Start'

export default {
  name: 'app',
  components: {
    Nav,
    Menu,
    TeamJoin,
    AccountLogin,
    AccountSignup,
    AccountResetPassword,
    AccountStart
  },
  computed: {
    direction () {
      return this.user ? 'vertical' : 'horizontal'
    },
    invitation () {
      return ((this.user || {}).invitations || []).pop()
    },
    ...mapState(['token', 'initializing', 'user', 'project', 'roleId'])
  },
  created () {
    if (this.$route.query.onelogin) {
      localStorage.setItem('role', this.$route.query.role)
      localStorage.setItem('token', this.$route.query.onelogin)

      this.$router.replace({ query: {} })
      window.location.href = '/'
    } else if (this.token) {
      this.$store.dispatch('auth')
    }
  },
  mounted () {
    window.onfocus = window.onpageshow = () => {
      if (moment().unix() > this.refreshTimeout) {
        this.$store.dispatch('load', true)
        this.refreshTimeout = moment().add(30, 'minutes').unix()
      }
    }
  },
  data () {
    return {
      signupStep: 1,
      refreshTimeout: moment().add(30, 'minutes').unix()
    }
  },
  watch: {
    '$route.query.roleId' (roleId) {
      if (roleId && this.user) {
        const role = this.user.roles.find((r) => r.id === roleId)
        if (role) {
          this.$store.dispatch('role', role)
        }
      }
    },
    roleId (value, old) {
      if (value) {
        if (old) {
          this.$sse.close()
        }

        this.$nextTick(this.sseConnect)
        this.$nextTick(() => this.$store.dispatch('load', true))
      }
    },
    user (value, old) {
      if (old && value && this.roleId) {
        this.$store.dispatch('role', value.roles.find((r) => r.id === this.roleId))
        this.$nextTick(() => this.$store.dispatch('load'))
      }
    }
  }
}
</script>

<style lang="scss">
  @import "./theme";

  #app, html, body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Ubuntu", sans-serif;
    font-weight: 400;
    font-size: 14px;
    background-color: #fff;
    color: $--color-text-primary;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    height: 100%;
    margin: 0;
    padding: 0;

    &.el-container, > .el-container {
      overflow: hidden;
    }

    input, textarea, keygen, select, button {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Ubuntu", sans-serif;
      font-weight: 400;

      &:focus {
        outline: none;
      }
    }

    .monospace {
      font-family: monospace;
    }

    h1, h2, h3, h4, h5, h6, th, strong {
      font-weight: 600;
    }

    a, a:focus, a:visited {
      color: $--color-primary;

      &:hover {
        opacity: 0.8;
      }
    }

    .el-main, .el-container, .el-dialog.is-fullscreen .el-dialog__body {
      height: 100%;
      padding: 0;
    }

    .el-aside.menu {
      overflow: hidden !important;

      .main-menu {
        padding-bottom: 50px;
      }
    }

    .el-header {
      background-color: $--background-color-base;
      border-bottom: 1px solid $--border-color-base;
    }

    .el-button.el-button--primary {
      font-weight: 500;
    }

    .el-button.el-button--default {
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    }

    .el-button.logo {
      //background-color: $--color-primary-alt;
      background-image: url(./assets/logo-icon-white.svg);
      background-repeat: no-repeat;
      background-size: 15px;
      background-position: left 5px center;
      padding-left: 40px;
      font-weight: 600 !important;
    }

    .el-button [class*="el-icon-"] + span, .el-button [class*="mdi-"] + span {
      margin-left: 5px;
    }

    .el-card {
      &.is-always-shadow, &.is-hover-shadow:hover {
        -webkit-box-shadow: $--card-box-shadow;
        box-shadow: $--card-box-shadow;
      }
      &.is-always-shadow {
        border-color: transparent;
      }

      &.no-padding {
        > .el-card__body {
          padding: 0;
        }
      }
    }

    .el-table, .el-dropdown {
      color: $--color-text-alt;
    }

    .el-table thead {
      color: $--color-text-alt;
    }

    .wrapper {
      height: 100%;

      > h1, > h2, > h3 {
        line-height: 40px;
        padding: 18px 48px 0 48px;
        margin: 0;

        .el-button {
          float: right;
        }
      }
      > .filters {
        margin: 18px 48px 12px;
      }

      > div {
        margin: 0 48px;

        &.el-table {
          margin: 24px 0;
        }
        &.el-dialog__wrapper {
          margin: 0;
        }
        &.el-card {
          margin: 20px 40px;
        }
      }

      > .el-menu.menu {
        margin: 18px 0;
        padding: 0 48px;
        background-color: $--background-color-base;
        border-top: 1px solid $--border-color-base;
        border-bottom: none;

        .el-menu-item {
          &:hover, &:focus {
            background-color: transparent;
          }
        }
      }
    }

    .el-dialog {
      &.is-fullscreen {
        max-width: 1400px;

        .el-dialog__footer {
          max-width: 1400px;
        }
      }

      .el-dialog__body {
        color: $--color-text-primary;
      }
    }

    .el-badge.support .el-badge__content {
      background-color: $--support-color-base;
    }

    .el-loading-spinner {
      i {
        font-size: 40px;

        &.el-icon-error {
          color: $--color-danger;
        }

        &.el-icon-warning {
          color: $--color-warning;
        }
      }

      .el-icon-error + .el-loading-text {
        color: $--color-danger;
      }
      .el-icon-warning + .el-loading-text {
        color: $--color-warning;
      }
    }

    .blur {
      -webkit-filter: blur(2px);
      -moz-filter: blur(2px);
      -o-filter: blur(2px);
      -ms-filter: blur(2px);
      filter: blur(2px);
    }

    .app {
      &.node {
        background-image: url(/bundles/node.svg);
      }
      &.php {
        background-image: url(/bundles/php.svg);
      }
      &.python {
        background-image: url(/bundles/python.svg);
      }
      &.ruby {
        background-image: url(/bundles/ruby.svg);
      }
      &.golang {
        background-image: url(/bundles/golang.svg);
      }
      &.java {
        background-image: url(/bundles/java.svg);
      }
      &.wordpress {
        background-image: url(/bundles/wordpress.svg);
      }
      &.drupal {
        background-image: url(/bundles/drupal.svg);
      }
      &.symfony {
        background-image: url(/bundles/symfony.svg);
      }
      &.laravel {
        background-image: url(/bundles/laravel.svg);
      }
      &.ez {
        background-image: url(/bundles/ez.svg);
      }
      &.typo3 {
        background-image: url(/bundles/typo3.svg);
      }
      &.git {
        background-image: url(/bundles/git.svg);
      }
      &.disabled {
        opacity: 0.2;
        filter: grayscale(100%);
        cursor: not-allowed;
      }
    }

    .align-center {
      text-align: center;
    }

    .align-left {
      text-align: left;
    }

    .align-right {
      text-align: right;
    }
  }
</style>

<style lang="scss" scoped>
  @import "./theme";

  @media screen and (max-width: 1200px) {
    .el-aside.menu {
      display: none;
    }
  }
  @media screen and (min-width: 1200px) {
    .el-header {
    }
  }

  #login, #signup {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;

    .el-form {
      width: 400px;
      height: 470px;
      margin: 0 auto;
      background: url(assets/logo.svg) no-repeat center top;
      background-size: 220px;
      padding-top: 75px;
    }

    .signup, .signin {
      border-top: 1px solid rgb(213, 216, 227);
      position: absolute;
      bottom: 30px;
      width: 400px;

      span, .el-button {
        font-size: 12px;
        margin-right: 5px;
      }
    }

    &#signup {
      .el-form, .signin {
        width: 560px;
      }
    }
  }

  #start {
     > div {
       background-color: #fff;
       border-radius: 20px;
       padding: 50px;
       width: 450px;
       margin: 80px auto 0;
     }
   }

  .el-aside {
    position: relative;
    height: 100%;

    img {
      max-height: 100%;
      max-width: calc(100% - 50px);
      width: auto;
      height: auto;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
      padding: 25px;
    }
  }
</style>
